import * as React from 'react'
import { Expense, ExpenseColumnConfig, ExpenseColumnProperty, ExpenseColumns, ExpenseProperty, ResourceRendererOptions, ResourceRendererResult, Settings } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import ResourceTableColor from './ResourceTableColor'
import showdown from '../../utilities/showdown'
import ExpenseHelper from '../../helpers/ExpenseHelper'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import styled from 'styled-components'
import ExpenseAvatar from '../Avatar/ExpenseAvatar'
import ModalFooterActionIcon from '../Modals/Parts/ModalFooterAction'
import ExpenseCategoryHelper from '../../helpers/ExpenseCategoryHelper'
import Icon from '../Icons/Icon'

const CategoryIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  i {
    color: currentColor;
  }

  svg {
    fill: currentColor;
  }
`

const CategoryIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-weight: 500;
`

const CategoryContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	align-items: center;
	width: 100%;
`

const CategoryAvatarContainer = styled.div`
	margin-right: 8px;
`

const CategoryContent = styled.div`
	display: flex;
	flex-direction: column;
`

const ExpenseName = styled.div`
	font-size: 15px;
	color: #212b36;
	cursor: pointer;
	padding: 0 4px;

	max-width: 250px;
	text-overflow: ellipsis;
	overflow: hidden;
`

const CategoryName = styled.div`
	font-weight: 500;
	font-size: 14px;
	cursor: pointer;
	flex: 1;
	flex-direction: row;
	padding: 0 4px;

	max-width: 265px;
	text-overflow: ellipsis;
	overflow: hidden;
`

const AttachmentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  > div {
    align-items: start;
  }

`

export const DEFAULT_EXPENSE_COLUMNS: ExpenseColumnProperty[] = [
  'name',
  'supplier',
  'invoiced_on',
  'status',
  'attachment',
  'amount'
]

const ExpenseColumnConfigs: Record<ExpenseColumnProperty, ExpenseColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('Expense::id') }),
    render: ({ expense }) => expense.id || '-'
  },
  color: {
    header: () => ({ title: i18n.t('Expense::color') }),
    render: ({ expense }) => <ResourceTableColor style={{ backgroundColor: ExpenseHelper.getColor(expense) }} />
  },
  name: {
    header: () => ({ title: i18n.t('Expense::Details') }),
    render: ({ expense }) => {
      let categoryStyles: { color?: string } = {}
      if (expense.category) {
        categoryStyles.color = expense.category.color
      }
      return (
        <CategoryContainer>
          <CategoryAvatarContainer>
            <ExpenseAvatar expense={expense} rounded={true} onClick={() => { }} />
          </CategoryAvatarContainer>

          <CategoryContent>
            {expense.category && <CategoryName style={categoryStyles}>
              {expense.category ? expense.category.name : ''}
            </CategoryName>}
            <ExpenseName title={expense.name}>
              {expense.name}
            </ExpenseName>
          </CategoryContent>
        </CategoryContainer>
      )
    },
  },
  type: {
    header: () => ({ title: i18n.t('Expense::type') }),
    render: ({ expense }) => expense.type ? i18n.t(`ExpenseType::${expense.type}`) : '-'
  },
  notes: {
    header: () => ({ title: i18n.t('Expense::notes') }),
    render: ({ expense }) => expense.notes ? <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(expense.notes) }} /> : '-'
  },
  invoiced_on: {
    header: () => ({ title: i18n.t('Expense::invoiced_on') }),
    render: ({ expense, setting }) => expense.invoiced_on ? moment(expense.invoiced_on).format(setting.date_format) : '-'
  },
  due_on: {
    header: () => ({ title: i18n.t('Expense::due_on') }),
    render: ({ expense, setting }) => expense.due_on ? moment(expense.due_on).format(setting.date_format) : '-'
  },
  paid_on: {
    header: () => ({ title: i18n.t('Expense::paid_on') }),
    render: ({ expense, setting }) => expense.paid_on ? moment(expense.paid_on).format(setting.date_format) : '-'
  },
  net_total: {
    header: () => ({ title: i18n.t('Expense::net_total'), align: 'right' }),
    render: ({ expense, setting }) => NumberFormatter.formatCurrency(expense.currency || setting.default_currency, setting.number_format, expense.net_total)
  },
  amount: {
    header: () => ({ title: i18n.t('Expense::amount'), align: 'right' }),
    render: ({ expense, setting }) => NumberFormatter.formatCurrency(expense.currency || setting.default_currency, setting.number_format, expense.amount)
  },
  billable: {
    header: () => ({ title: i18n.t('Expense::billable') }),
    render: ({ expense }) => expense.billable ? i18n.t('Yes') : i18n.t('No')
  },
  markup: {
    header: () => ({ title: i18n.t('Expense::markup') }),
    render: ({ expense }) => expense.markup ? `${Number(expense.markup) * 100}%` : '-'
  },
  billable_amount: {
    header: () => ({ title: i18n.t('Expense::billable_amount'), align: 'right' }),
    render: ({ expense, setting }) => {
      const billableAmount = ExpenseHelper.getBillableAmount(expense)
      return billableAmount ? NumberFormatter.formatCurrency(expense.currency || setting.default_currency, setting.number_format, billableAmount) : '-'
    }
  },
  billed: {
    header: () => ({ title: i18n.t('Expense::billed') }),
    render: ({ expense }) => ExpenseHelper.getBillableBadge(expense)
  },
  supplier: {
    header: () => ({ title: i18n.t('Expense::supplier') }),
    render: ({ expense }) => expense.supplier?.name || '-'
  },
  category: {
    header: () => ({ title: i18n.t('Expense::category') }),
    render: ({ expense }) => {
      const color = ExpenseHelper.getColor(expense)
      const icon = expense.category ? ExpenseCategoryHelper.getIcon(expense.category, color) : <Icon icon='question-mark' />
      return (
        <CategoryIconContainer style={{ color: color }}>
          <CategoryIcon>
            {icon}
          </CategoryIcon>
          {expense.category?.name}
        </CategoryIconContainer>
      )
    }
  },
  currency: {
    header: () => ({ title: i18n.t('Expense::currency') }),
    render: ({ expense }) => expense.currency || '-'
  },
  booked_on: {
    header: () => ({ title: i18n.t('Expense::booked_on') }),
    render: ({ expense, setting }) => expense.booked_on ? moment(expense.booked_on).format(setting.date_format) : '-'
  },
  contact: {
    header: () => ({ title: i18n.t('Expense::contact') }),
    render: ({ expense }) => expense.contact?.name || '-'
  },
  project: {
    header: () => ({ title: i18n.t('Expense::project') }),
    render: ({ expense }) => expense.project?.name || '-'
  },
  remittance_information: {
    header: () => ({ title: i18n.t('Expense::remittance_information') }),
    render: ({ expense }) => expense.remittance_information || '-'
  },
  peppol_id: {
    header: () => ({ title: i18n.t('Expense::peppol_id') }),
    render: ({ expense }) => expense.peppol_id || '-'
  },
  created_at: {
    header: () => ({ title: i18n.t('Expense::created_at') }),
    render: ({ expense, setting }) => expense.created_at ? moment(expense.created_at).format(setting.date_format) : '-'
  },
  reviewed: {
    header: () => ({ title: i18n.t('Expense::reviewed') }),
    render: ({ expense }) => expense.reviewed ? i18n.t('Yes') : i18n.t('No')
  },
  attachment: {
    header: () => ({ title: i18n.t('Expense::attachment') }),
    render: ({ expense }) => (
      <AttachmentContainer>
        <ModalFooterActionIcon
          icon='attachment'
          tooltip={i18n.t('Expense::Receipt attached')}
          active={Boolean(expense.attachment_url)}
          disabled={!Boolean(expense.attachment_url)}
          disabledTooltip={i18n.t('Expense::Missing receipt')}
        />
      </AttachmentContainer>
    )
  },
  status: {
    header: () => ({ title: i18n.t('Expense::status') }),
    render: ({ expense }) => ExpenseHelper.getStatusBadge(expense)
  }
}

export function getExpenseTableRenderer(options: ResourceRendererOptions<Expense>): ResourceRendererResult<Expense, ExpenseProperty> {
  const {
    setting,
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = ExpenseColumns.map(column => ({ label: i18n.t(`Expense::${column}`), value: column }))
  const selectedColumns: ExpenseProperty[] = LocalStorage
    .get(LocalStorageKey.EXPENSE_COLUMNS, DEFAULT_EXPENSE_COLUMNS)
    .filter((column) => ExpenseColumnConfigs[column])

  const headers: ResourceTableHeader[] = selectedColumns
    .map(column => ({
      ...ExpenseColumnConfigs[column].header(),
      visible: true
    }))

  headers.push({ title: '', stickyRight: '0px' })

  const renderRow = (expense: Expense, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(expense.id)
    let actions: IActionListItem[] = [
      { key: 'pay', icon: 'money-bill', content: expense?.payment_id ? i18n.t('Expenses::View payment') : i18n.t('Expenses::Queue payment') },
      { key: 'edit', icon: 'edit-solid', content: i18n.t('ExpensesTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('ExpensesTable::Delete'), destructive: true }
    ]

    const applyMaxWidthColumns: ExpenseProperty[] = [
      'name',
      'notes',
      'supplier',
      'contact',
      'project',
      'remittance_information'
    ]

    let cells: React.ReactNode[] = selectedColumns.map((column) => (
      <ResourceTableRowData
        key={`${expense.id}-${column}`}
        onClick={onRowClick ? () => onRowClick(expense) : undefined}
        maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
        ellipse={applyMaxWidthColumns.includes(column)}
        textAlign={ExpenseColumnConfigs[column].header().align}
      >
        {ExpenseColumnConfigs[column].render({ expense, ...options })}
      </ResourceTableRowData>
    ))

    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${expense.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, expense)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={expense.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, expense.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getExpenseTableRenderer
